import {
  faCalendar,
  faCheckCircle,
  faImage,
  faPencil,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import {
  faClock,
  faCircleCheck,
  faXmarkCircle,
} from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  EXAM_RESULT_APPROVED_STATUS,
  EXAM_RESULT_PENDING_STATUS,
  EXAM_RESULT_REJECTED_STATUS,
  PRIMARY_SECONDARY_GRADES,
} from '@utils/constants/subject.constants';
import React, { useEffect, useRef, useState } from 'react';
import { IStudentExamResultImageFile } from '@models/interfaces/student.interface';
import { Button, PopUp, Sheet, Snackbar } from 'geniebook-ui/dist';
import { useViewWidth } from '@utils/helpers/viewWidth';
import ImageAttachment from '@pages/exam-result/ImageAttachment';
import Times from '@assets/icons/times';
import ChevronLeft from '@assets/icons/chevron-left';
import ChevronRight from '@assets/icons/chevron-right';
import { useDeleteExamResult } from '@hooks/student.hook';
import { useAtom } from 'jotai';
import { studentHasActiveSubscriptionAtom } from '@atom/examResultAtom';
import { useNavigate } from 'react-router-dom';

const breakpoint = require('geniebook-ui/dist/themes/breakpoint');

export interface ExamResultTableItemProps {
  id: string;
  subject: string;
  subject_color: string;
  level: string;
  type: string;
  exam_date: moment.Moment;
  marks?: number | null;
  max_marks?: number | null;
  grade_id?: number;
  image_files?: IStudentExamResultImageFile[] | null;
  approval: number;
  rejection_reason: string;
}

type ButtonGroupProps = {
  hideModal: () => void;
  setModalProps: (params: ExamResultTableItemProps | null) => void;
  setDeleteExamResultID: (params: string | null) => void;
  examResult: ExamResultTableItemProps | null;
};

interface SheetWrapperProps {
  onClickOutside: (target: EventTarget | null) => void;
  children: React.ReactNode;
  excludeSelector?: string;
}

const getGradeNameById = (gradeId: number): string =>
  PRIMARY_SECONDARY_GRADES.find(grade => grade.grade_id == gradeId)?.name ??
  '-';

const ButtonGroup = ({
  setDeleteExamResultID,
  examResult,
}: ButtonGroupProps) => {
  const navigate = useNavigate();
  if (!examResult) {
    return null;
  }

  return (
    <div className={'w-full px-4'}>
      {examResult.approval == EXAM_RESULT_PENDING_STATUS && (
        <Button
          className="!max-w-[100%] w-full"
          onClick={() => {
            navigate(`/exam-result/edit/${examResult.id}`);
          }}
        >
          <FontAwesomeIcon icon={faPencil} className="mr-1" />
          Edit
        </Button>
      )}

      {(examResult.approval == EXAM_RESULT_PENDING_STATUS ||
        examResult.approval == EXAM_RESULT_REJECTED_STATUS) && (
        <Button
          variant="secondary"
          className="!max-w-[100%] w-full !text-alert-error !hover:text-alert-error"
          onClick={() => {
            setDeleteExamResultID(examResult.id);
          }}
        >
          <FontAwesomeIcon icon={faTrash} className="mr-1 " />
          Delete
        </Button>
      )}
    </div>
  );
};

const SheetWrapper: React.FC<SheetWrapperProps> = ({
  onClickOutside,
  children,
  excludeSelector,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target || !ref.current || ref.current.contains(target)) return;

      if (excludeSelector) {
        const excludedElements = document.querySelectorAll(excludeSelector);
        const excludedArray = Array.from(excludedElements); // convert NodeList to Array
        for (const excludedElement of excludedArray) {
          if (excludedElement.contains(target)) return;
        }
      }

      onClickOutside(target);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClickOutside, excludeSelector]);

  return (
    <div id="sheetWrapper" ref={ref}>
      {children}
    </div>
  );
};

const ExamResultTableItem = (props: ExamResultTableItemProps) => {
  const [showModal, setShowModal] = React.useState<boolean>(false);
  const [deleteExamResultID, setDeleteExamResultID] = React.useState<
    string | null
  >(null);
  const [modalProps, setModalProps] =
    React.useState<ExamResultTableItemProps | null>(null);
  const [selectedImage, setSelectedImage] = useState('');
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(-1);
  const deleteExamResult = useDeleteExamResult();
  const imageFiles = props.image_files ?? [];
  const [studentHasActiveSubscription] = useAtom(
    studentHasActiveSubscriptionAtom,
  );

  type ErrorResp = {
    error: boolean;
    error_msg: string;
    error_code: string;
  };

  type SuccessResp = {
    success: boolean;
    success_message: string;
  };
  const [error, setError] = React.useState<ErrorResp>({
    error: false,
    error_msg: '',
    error_code: '',
  });

  const [success, setSuccess] = React.useState<SuccessResp>({
    success: false,
    success_message: '',
  });
  const dismissSnackbar = () => {
    window.location.reload();
  };

  const renderSnackbar = ({
    error,
    success,
  }: {
    error: ErrorResp;
    success: SuccessResp;
  }) => {
    const state = error.error
      ? 'error'
      : success.success
      ? 'celebratory'
      : null;
    const message = error.error ? error.error_msg : success.success_message;
    const buttonText = error.error ? 'Refresh' : '';
    return (
      state && (
        <Snackbar
          className={'fixed center-horizontal top-[35px]'}
          onTextClick={dismissSnackbar}
          states={state}
          message={message}
          buttonText={buttonText}
          views="desktop"
        />
      )
    );
  };

  const onExamResultDelete = async (examResultID: string) => {
    try {
      const formData = new FormData();
      formData.append('student_exam_score_id', examResultID + '');
      const res = await deleteExamResult.mutateAsync(formData);
      const body = res.data;
      if (body.error) {
        setError(prevError => ({
          ...prevError,
          error: body.error,
          error_msg: body.error_msg,
          error_code: body.error_code,
        }));
      } else {
        setSuccess(prevSuccess => ({
          ...prevSuccess,
          success: true,
          success_message: 'Exam Result Deleted Successfully',
        }));

        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      setError(prevError => ({
        ...prevError,
        error: true,
        error_msg: 'Unknown Error',
        error_code: '',
      }));
    }
  };

  const viewWidth = useViewWidth();
  const handleShowModal = () => {
    setShowModal(true);
  };

  const hideModal = () => {
    setShowModal(false);
  };

  const hideModalDelete = () => {
    setDeleteExamResultID(null);
  };

  const getModalBody = (props: ExamResultTableItemProps) => {
    return (
      <div className={'text-primary-navyblue w-full text-[14px]'}>
        <div className={'flex flex-row gap-2'}>
          <div className="flex text-center font-bold">{props.subject}</div>
          <div className="flex text-center font-bold">{props.level}</div>
          <div
            className={`flex text-center items-center text-xs gap-1 text-${
              props.approval == EXAM_RESULT_APPROVED_STATUS
                ? 'alert-success'
                : props.approval == EXAM_RESULT_REJECTED_STATUS
                ? 'alert-error'
                : 'neutral-eraser'
            } justify-end ml-auto`}
          >
            <div className={`flex text-center pt-[2px]`}>
              <FontAwesomeIcon
                icon={
                  props.approval == EXAM_RESULT_APPROVED_STATUS
                    ? faCircleCheck
                    : props.approval == EXAM_RESULT_REJECTED_STATUS
                    ? faXmarkCircle
                    : faClock
                }
              />
            </div>
            <div className="flex text-center">
              {props.approval == EXAM_RESULT_APPROVED_STATUS
                ? 'Approved'
                : props.approval == EXAM_RESULT_REJECTED_STATUS
                ? 'Rejected'
                : 'Waiting for approval'}
            </div>
          </div>
        </div>
        <div className={'flex flex-row gap-1'}>
          <div className="flex text-center font-bold ">
            {props.exam_date.format('YYYY')}
          </div>
          <div className="flex text-center font-bold ">{props.type}</div>
        </div>
        <div className={'flex flex-row gap-1'}>
          <div className="flex text-center text-primary-apricot-dark pt-[6px]">
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>
          <div className="flex text-center">
            {props.marks?.toString() ?? '-'}/
            {props.max_marks?.toString() ?? '-'}{' '}
            {`(${
              props.marks !== undefined && props.max_marks !== undefined
                ? props.max_marks === 0
                  ? '0'
                  : (
                      ((props.marks ?? 0) / (props.max_marks ?? 1)) *
                      100
                    ).toFixed(0)
                : '0'
            }%)`}
          </div>
          <div className={`flex text-center text-xs gap-1 justify-end ml-auto`}>
            {getGradeNameById(props.grade_id ?? 0)}
          </div>
        </div>
        <div className={'flex flex-row gap-1'}>
          <div className="flex text-center text-primary-skyblue pt-[6px]">
            <FontAwesomeIcon icon={faCalendar} />
          </div>
          <div className="flex text-center">
            {props.exam_date.format('DD MMM YYYY')}
          </div>
        </div>

        <div
          className={
            'flex flex-row gap-1 text-sm border-t-neutral-eraser border-t mt-3'
          }
        >
          <ImageAttachment
            image_files={props.image_files ?? []}
            setSelectedImage={setSelectedImage}
            setSelectedImageIndex={setSelectedImageIndex}
          />
        </div>

        {props.approval == EXAM_RESULT_REJECTED_STATUS && (
          <div
            className={'font-bold mb-3 border-t-neutral-eraser border-t pt-3'}
          >
            <div className={'flex flex-row gap-1'}>Reason for Rejection:</div>
            <div
              className={
                'flex flex-row gap-1 text-sm text-left font-thin break-words whitespace-pre-wrap max-w-full overflow-y-auto'
              }
            >
              {props.rejection_reason ? props.rejection_reason : '-'}
            </div>
          </div>
        )}
      </div>
    );
  };
  return (
    <>
      <div
        className={
          'w-full py-3 px-4 flex flex-row items-center gap-4 font-bold text-sm hover:bg-primary-navyblue-shade/[0.65] cursor-pointer rounded-2xl max-lg:text-xs max-sm:justify-center max-sm:!pl-7'
        }
        onClick={() => {
          handleShowModal();
          setModalProps(props);
        }}
      >
        <div className="flex-none w-[15%] truncate max-sm:w-[15%]">
          {props.subject}
        </div>
        <div className={'flex-none w-[8%] max-sm:w-[6%]'}>{props.level}</div>
        <div className={'flex-none w-[10%] text-center max-sm:w-[12%]'}>
          {props.type}
        </div>
        <div className={'flex-none w-[15%] text-center max-sm:w-[20%]'}>
          {window.innerWidth <= 640
            ? props.exam_date.format('MMM YYYY')
            : props.exam_date.format('DD MMM YYYY')}
        </div>
        <div className={'flex-none w-[15%] text-center max-sm:w-[20%]'}>
          {props.max_marks === 0
            ? '-/-'
            : `${props.marks?.toString() ?? '-'}/${
                props.max_marks?.toString() ?? '-'
              } (${
                props.marks !== undefined && props.max_marks !== undefined
                  ? (
                      ((props.marks ?? 0) / (props.max_marks ?? 1)) *
                      100
                    ).toFixed(0)
                  : '0'
              }%)`}
        </div>
        <div className={'flex-none w-[10%] text-center max-sm:hidden'}>
          {getGradeNameById(props.grade_id ?? 0)}
        </div>
        <div
          className={`flex-none w-[8%] text-center items-center ${
            props.image_files ? 'text-primary-apricot' : 'text-neutral-eraser'
          } max-sm:hidden`}
        >
          <FontAwesomeIcon
            icon={faImage}
            data-testid="hard-icon"
            className={'mr-1'}
          />
          View
        </div>
        <div className={'flex-none w-[5%]'}>
          <FontAwesomeIcon
            icon={
              props.approval == EXAM_RESULT_PENDING_STATUS
                ? faClock
                : props.approval == EXAM_RESULT_APPROVED_STATUS
                ? faCircleCheck
                : faXmarkCircle
            }
            className={
              props.approval == EXAM_RESULT_PENDING_STATUS
                ? 'text-neutral-eraser'
                : props.approval == EXAM_RESULT_APPROVED_STATUS
                ? 'text-alert-success'
                : 'text-alert-error'
            }
            data-testid={'hard-icon'}
          />
        </div>
      </div>

      {/* Modal */}
      {showModal &&
        (viewWidth > breakpoint.lg ? (
          <PopUp
            buttonGroup={
              studentHasActiveSubscription && (
                <ButtonGroup
                  hideModal={hideModal}
                  setModalProps={setModalProps}
                  examResult={modalProps}
                  setDeleteExamResultID={setDeleteExamResultID}
                />
              )
            }
            onClose={hideModal}
            width={viewWidth < breakpoint.md ? `${viewWidth - 30}px` : '400px'}
            title={'Exam Details'}
            description={modalProps !== null && getModalBody(modalProps)}
            disableBodyScroll={true}
            className="!px-8 !py-6"
          />
        ) : (
          <Sheet
            isOpen={showModal}
            toggleSheet={() => {
              hideModal();
            }}
            zIndex={99}
            darkMode={false}
            className={`modal-wrapper ${
              showModal ? 'animate-slide-up' : 'animate-slide-down'
            }`}
          >
            <SheetWrapper
              onClickOutside={hideModal}
              excludeSelector=".exclude-element"
            >
              <div
                className={
                  'p-4 !pb-[75px] w-full text-center max-h-[80vh] overflow-auto'
                }
              >
                <div
                  className={'font-black mb-4 text-primary-navyblue text-lg'}
                >
                  Exam Details
                </div>
                <div>{modalProps !== null && getModalBody(modalProps)}</div>
                {studentHasActiveSubscription && (
                  <ButtonGroup
                    hideModal={hideModal}
                    setModalProps={setModalProps}
                    examResult={modalProps}
                    setDeleteExamResultID={setDeleteExamResultID}
                  />
                )}
              </div>
            </SheetWrapper>
          </Sheet>
        ))}

      {deleteExamResultID && (
        <PopUp
          buttonGroup={
            <>
              <Button
                variant="primary"
                className="!max-w-[100%] w-full !hover:text-neutral-chalk bg-alert-error !text-neutral-chalk"
                onClick={() => {
                  onExamResultDelete(deleteExamResultID);
                  hideModalDelete();
                  hideModal();
                }}
              >
                <FontAwesomeIcon icon={faTrash} className="mr-1" />
                Delete
              </Button>
              <Button
                variant="secondary"
                className="!max-w-[100%] w-full"
                onClick={() => {
                  hideModalDelete();
                }}
              >
                Cancel
              </Button>
            </>
          }
          onClose={hideModalDelete}
          width={viewWidth < breakpoint.md ? `${viewWidth - 50}px` : '300px'}
          title={'Delete Entry'}
          description={'This action cannot be undone'}
          disableBodyScroll={true}
          className="!px-8 !py-6"
        />
      )}

      {selectedImage.length > 0 && imageFiles && (
        <div className="fixed w-screen h-screen z-100 left-0 top-0 bottom-0 right-0">
          <div
            className="fixed top-0 left-0 right-0 bottom-0 h-full w-full opacity-50 bg-gray-700 cursor-zoom-out"
            onClick={() => {
              setSelectedImage('');
              setSelectedImageIndex(-1);
            }}
          ></div>
          <div className="flex items-center justify-center h-full w-full p-8 overflow-auto">
            <div className="w-full max-w-screen-sm max-h-full shadow-lg rounded-lg z-10 bg-white">
              <img
                className="w-full h-full object-contain"
                src={selectedImage}
                alt="Answer Zoom In"
              />
            </div>
          </div>
          <Times
            className="absolute top-3 right-4 w-4 h-8 cursor-pointer text-white"
            style={{ fill: 'currentcolor' }}
            onClick={() => {
              setSelectedImage('');
              setSelectedImageIndex(-1);
            }}
          />
          {imageFiles.length > 1 && (
            <>
              <div
                className="absolute top-1/2 left-6 w-10 h-10 bg-neutral-eraser rounded-full flex justify-center items-center cursor-pointer"
                style={{ transform: 'translateY(-50%)' }}
                onClick={() => {
                  let newSelectedIndex = selectedImageIndex - 1;
                  if (newSelectedIndex < 0)
                    newSelectedIndex = imageFiles.length - 1;
                  setSelectedImage(imageFiles[newSelectedIndex].image_url);
                  setSelectedImageIndex(newSelectedIndex);
                }}
              >
                <ChevronLeft
                  className="text-white w-[10px] h-4"
                  style={{ fill: 'currentcolor' }}
                />
              </div>
              <div
                className="absolute top-1/2 right-6 w-10 h-10 bg-neutral-eraser rounded-full flex justify-center items-center cursor-pointer"
                style={{ transform: 'translateY(-50%)' }}
                onClick={() => {
                  let newSelectedIndex = selectedImageIndex + 1;
                  if (newSelectedIndex > imageFiles.length - 1)
                    newSelectedIndex = 0;
                  setSelectedImage(imageFiles[newSelectedIndex].image_url);
                  setSelectedImageIndex(newSelectedIndex);
                }}
              >
                <ChevronRight
                  className="text-white w-[10px] h-4"
                  style={{ fill: 'currentcolor' }}
                />
              </div>
            </>
          )}
        </div>
      )}

      {renderSnackbar({ error, success })}
    </>
  );
};

export default ExamResultTableItem;
